import { coerceEvmAddress, detectChainCandidates } from './chainDetect';

export type SearchInputType =
    | 'evm_address'
    | 'tron_address'
    | 'btc_address'
    | 'sol_address'
    | 'xrp_address'
    | 'ton_address'
    | 'xmr_address'
    | 'zec_address'
    | 'dash_address'
    | 'doge_address'
    // Generic recognised-chain wallet (LTC, BCH, FIRO, ADA, COSMOS, XLM, …) —
    // chains the canonical detector knows but that have no dedicated badge type.
    // Before unification these fell through to the SOL catch-all and were
    // mislabeled; now they carry the correct `chain` with a generic type.
    | 'crypto_address'
    | 'evm_tx'
    | 'tron_tx'
    | 'btc_tx'
    | 'sol_tx'
    | 'upi_id'
    | 'email'
    | 'phone'
    | 'unknown';

export interface DetectResult {
    type: SearchInputType;
    label: string;
    chain?: string;
    isTx: boolean;
    /** For ambiguous inputs: list of chains this could belong to */
    possibleChains?: string[];
    /**
     * Address / tx / UPI substring after stripping an optional chain qualifier
     * (`MATIC 0x…`, `MATIC:0x…`, CSV `0x…,MATIC,…`). Same as the trimmed input
     * when no qualifier is present.
     */
    query?: string;
    /** True when the user named a chain in the input (not the EVM ETH default). */
    chainExplicit?: boolean;
}

/** Aliases accepted as an omnibar / CSV chain qualifier. */
const CHAIN_HINT_ALIASES: Record<string, string> = {
    ETHEREUM: 'ETH', ETH: 'ETH', ERC20: 'ETH',
    TRON: 'TRX', TRX: 'TRX',
    BITCOIN: 'BTC', BTC: 'BTC',
    POLYGON: 'MATIC', MATIC: 'MATIC', POL: 'MATIC',
    BINANCE: 'BNB', BSC: 'BNB', BNB: 'BNB',
    OPBNB: 'OPBNB', OP_BNB: 'OPBNB',
    ARBITRUM: 'ARB', ARB: 'ARB',
    OPTIMISM: 'OP', OP: 'OP',
    BASE: 'BASE',
    AVAX: 'AVAX', AVALANCHE: 'AVAX',
    LINEA: 'LINEA', BLAST: 'BLAST', SCROLL: 'SCROLL',
    GNOSIS: 'GNOSIS', XDAI: 'GNOSIS',
    CELO: 'CELO', MANTLE: 'MANTLE', MOONBEAM: 'MOONBEAM',
    FTM: 'FTM', FANTOM: 'FTM',
    XRP: 'XRP', TON: 'TON', SOLANA: 'SOL', SOL: 'SOL',
    LTC: 'LTC', DOGE: 'DOGE', DASH: 'DASH', ZEC: 'ZEC',
    XMR: 'XMR', BCH: 'BCH', FIRO: 'FIRO',
};

function chainHintFromToken(raw: string | undefined | null): string | undefined {
    if (!raw) return undefined;
    return CHAIN_HINT_ALIASES[raw.trim().toUpperCase()];
}

/**
 * Pull an explicit chain off pasted input so EVM addresses are not silently
 * investigated as ETH. Benchmark / CSV rows look like
 * `0xe780…,MATIC,exchange,OKX Polygon Hot`.
 *
 * Does not split `bitcoincash:qp…` — `BITCOINCASH` is not a hint alias.
 */
export function parseChainQualifiedQuery(raw: string): { query: string; chainHint?: string } {
    const val = raw.trim();
    if (!val) return { query: val };

    const csv = val.split(',');
    if (csv.length >= 2) {
        const first = csv[0].trim();
        const second = csv[1].trim();
        const hintAfter = chainHintFromToken(second);
        if (hintAfter && detectChainCandidates(first).length > 0) {
            return { query: first, chainHint: hintAfter };
        }
        const hintBefore = chainHintFromToken(first);
        if (hintBefore && detectChainCandidates(second).length > 0) {
            return { query: second, chainHint: hintBefore };
        }
    }

    const prefix = val.match(/^([A-Za-z][A-Za-z0-9]{1,15})[:/\s]+(\S.*)$/);
    if (prefix) {
        const hint = chainHintFromToken(prefix[1]);
        const rest = prefix[2].trim();
        if (hint && detectChainCandidates(rest).length > 0) {
            return { query: rest, chainHint: hint };
        }
    }

    const suffix = val.match(/^(\S+)\s+([A-Za-z][A-Za-z0-9]{1,15})$/);
    if (suffix) {
        const hint = chainHintFromToken(suffix[2]);
        const rest = suffix[1].trim();
        if (hint && detectChainCandidates(rest).length > 0) {
            return { query: rest, chainHint: hint };
        }
    }

    return { query: val };
}

const EVM_QUALIFIER_CHAINS = new Set([
    'ETH', 'MATIC', 'BNB', 'BSC', 'OPBNB', 'ARB', 'BASE', 'OP', 'AVAX', 'FTM',
    'LINEA', 'SCROLL', 'ZKSYNC', 'BLAST', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM',
]);

function applyExplicitChain(result: DetectResult, chainHint?: string): DetectResult {
    if (!chainHint) return result;
    if (result.type === 'evm_address' || result.type === 'evm_tx') {
        if (!EVM_QUALIFIER_CHAINS.has(chainHint)) return result;
        return { ...result, chain: chainHint, chainExplicit: true };
    }
    const candidates = result.possibleChains
        ?? (result.chain ? [result.chain] : []);
    const compatible = candidates.length === 0
        || candidates.includes(chainHint)
        || result.chain === chainHint;
    if (!compatible) return result;
    return { ...result, chain: chainHint, chainExplicit: true };
}

// Maps a canonical chain symbol (from detectChainCandidates) to the omnibar's
// badge type + label. Chains without a dedicated badge get the generic
// `crypto_address` type with a "<CHAIN> Wallet" label — the important thing is
// the correct `chain`, which drives every downstream lookup. TRX uses the
// "TRON" wording the UI/tests expect (the symbol is TRX).
const ADDRESS_BADGE: Record<string, { type: SearchInputType; label: string }> = {
    TRX:  { type: 'tron_address', label: 'TRON Wallet' },
    BTC:  { type: 'btc_address',  label: 'BTC Wallet' },
    DOGE: { type: 'doge_address', label: 'DOGE Wallet' },
    DASH: { type: 'dash_address', label: 'DASH Wallet' },
    ZEC:  { type: 'zec_address',  label: 'ZEC Wallet' },
    XMR:  { type: 'xmr_address',  label: 'XMR Wallet' },
    XRP:  { type: 'xrp_address',  label: 'XRP Wallet' },
    SOL:  { type: 'sol_address',  label: 'SOL Wallet' },
    TON:  { type: 'ton_address',  label: 'TON Wallet' },
};

// Display-only hint list for the EVM badge. The real per-chain disambiguation
// happens via the chain_probe service (Workbench.loadInitialGraph), which calls
// detectChainCandidates directly; this list is purely the "/ETH/MATIC/…" badge
// suffix, kept short for legibility.
const EVM_BADGE_HINT = ['ETH', 'MATIC', 'BNB', 'ARB', 'BASE'];

export const detectInputType = (input: string): DetectResult => {
    const raw = input.trim();
    if (!raw) return { type: 'unknown', label: '', isTx: false };

    // 1. UPI ID — classify on the raw string so `user@bank` is never treated
    // as a chain-qualified address.
    if (/^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z]{3,}$/.test(raw)) {
        return { type: 'upi_id', label: 'UPI ID', isTx: false, query: raw };
    }

    // 2. Email Address
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
        return { type: 'email', label: 'Email', isTx: false, query: raw };
    }

    // 3. Indian Phone Number (Basic E.164 or local 10-digit)
    if (/^(\+91[\-\s]?)?[6789]\d{9}$/.test(raw)) {
        return { type: 'phone', label: 'Phone', isTx: false, query: raw };
    }

    const { query, chainHint } = parseChainQualifiedQuery(raw);
    const val = coerceEvmAddress(query);
    const finish = (result: DetectResult): DetectResult =>
        applyExplicitChain({ ...result, query: val }, chainHint);

    // 4. Wallet address — SINGLE SOURCE OF TRUTH.
    //
    // All address-format classification is delegated to the canonical detector
    // `detectChainCandidates()` (chainDetect.ts), which is checksum-aware
    // (base58check version byte + ed25519 curve check, see base58check.ts) and
    // shared with the backend rules. The omnibar previously carried its own
    // parallel regex ladder here; the two drifted (the DOGE→SOL bug was exactly
    // a chain present in chainDetect but missing here), and several chains
    // (LTC/BCH/FIRO/ADA/COSMOS/XLM) silently fell through to the SOL catch-all.
    // Delegating kills that whole class of divergence: add a chain in ONE place.
    const candidates = detectChainCandidates(val);
    if (candidates.length > 1) {
        // Only the EVM hex family is ambiguous (multiple candidates). Probe-time
        // disambiguation happens downstream; here it's one badge.
        return finish({ type: 'evm_address', label: 'EVM Wallet', chain: 'ETH', isTx: false, possibleChains: EVM_BADGE_HINT });
    }
    if (candidates.length === 1) {
        const chain = candidates[0];
        const badge = ADDRESS_BADGE[chain] ?? { type: 'crypto_address' as SearchInputType, label: `${chain} Wallet` };
        return finish({ type: badge.type, label: badge.label, chain, isTx: false });
    }

    // 4d. Partial/truncated address warning — only reached when the input isn't
    // a complete, recognised address. Helps the live badge nudge mid-typing.
    if (/^(0x|T|bc1|r)[a-zA-Z0-9]{3,20}$/.test(val) && val.length < 25) {
        return finish({ type: 'unknown', label: 'Incomplete Address', isTx: false });
    }

    // 8. EVM Transaction Hash (0x + 64 hex = 66 chars)
    if (/^0x[a-fA-F0-9]{64}$/.test(val)) {
        return finish({ type: 'evm_tx', label: 'EVM Tx', chain: 'ETH', isTx: true, possibleChains: ['ETH', 'MATIC', 'BNB', 'ARB', 'BASE'] });
    }

    // 9. TRON / BTC / EVM-without-0x / TON cell-hash (64 hex chars, ambiguous)
    if (/^[a-fA-F0-9]{64}$/.test(val)) {
        return finish({ type: 'tron_tx', label: 'Tx Hash', isTx: true, possibleChains: ['TRX', 'BTC', 'ETH', 'TON'] });
    }

    // 9b. TON BOC / base64 tx hash (+, /, or = — distinct from SOL base58)
    if (val.length >= 40 && val.length <= 50 && /[+/=]/.test(val) && /^[A-Za-z0-9+/=]+$/.test(val)) {
        return finish({ type: 'tron_tx', label: 'TON Tx', chain: 'TON', isTx: true });
    }

    // 10. Solana Transaction Hash (Base58, 45-88 chars — above address range)
    if (/^[1-9A-HJ-NP-Za-km-z]{45,88}$/.test(val)) {
        return finish({ type: 'sol_tx', label: 'SOL Tx', chain: 'SOL', isTx: true });
    }

    return finish({ type: 'unknown', label: 'Unknown', isTx: false });
};

export const OMNIBAR_EVM_PICKER_CHAINS = [
    'ETH', 'MATIC', 'BNB', 'OPBNB', 'ARB', 'BASE', 'OP', 'AVAX',
    'LINEA', 'BLAST', 'SCROLL', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM', 'FTM',
] as const;

/** Chains shown in the omnibar native select, or null when the input is unambiguous. */
export function omnibarPickerChains(detection: DetectResult): string[] | null {
    if (detection.type === 'evm_address' || detection.type === 'evm_tx') {
        const base: string[] = [...OMNIBAR_EVM_PICKER_CHAINS];
        if (detection.chain && !base.includes(detection.chain)) {
            return [detection.chain, ...base];
        }
        return base;
    }
    if (detection.possibleChains && detection.possibleChains.length > 1) {
        return detection.possibleChains;
    }
    return null;
}

/** Stable identity so checksum/whitespace tweaks do not wipe the chain pick. */
export function omnibarQueryIdentity(query: string): string {
    const q = (query || '').trim();
    if (/^0x[a-fA-F0-9]{40}$/.test(q)) return q.toLowerCase();
    return q;
}

/**
 * Chain shown in the EVM picker. User pick wins, then URL/session, then format detection.
 * pickedChain must be the select value — never format-default detection.chain.
 */
export function omnibarShownChain(
    pickedChain: string | null | undefined,
    sessionChain: string | null | undefined,
    detectedChain: string | null | undefined,
): string | null {
    const picked = (pickedChain || '').toUpperCase();
    if (picked) return picked;
    const session = (sessionChain || '').toUpperCase();
    // Do not hydrate TON from a previous TON session onto a hex hash picker.
    if (session && session !== 'TON') return session;
    const detected = (detectedChain || '').toUpperCase();
    return detected || null;
}

/** Chain hint for tx search. Empty string = backend auto-search. */
export function resolveOmnibarChainHint(
    detected: DetectResult,
    pickerChain?: string | null,
    sessionChain?: string | null,
): string {
    const picker = (pickerChain || '').toUpperCase();
    const session = (sessionChain || '').toUpperCase();

    if (detected.type === 'tron_tx') {
        if (picker === 'TRX' || picker === 'BTC' || picker === 'ETH' || picker === 'TON') {
            return picker;
        }
        if (detected.chain === 'TON') return 'TON';
        return '';
    }

    const formatCands = detectChainCandidates(detected.query || '');
    const unambiguous = formatCands.length === 1 ? formatCands[0] : null;
    if (unambiguous && unambiguous !== 'ETH') return unambiguous;
    if (picker && picker !== 'TON') return picker;
    if (unambiguous) return unambiguous;
    if (detected.chain && detected.chain !== 'ETH') return detected.chain;
    if (session && session !== 'ETH' && session !== 'TON') return session;
    return detected.chain || session || 'ETH';
}
